import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import { ENV } from "../config/envVariables";

import { formatLanguageCode } from "./utils";

type SupportedLanguages = Record<
  string,
  {
    regions: Region[];
    default: string;
  }
>;
interface Region {
  id: string;
  name: string;
}
interface LocaleResult {
  locale: string;
  usedFallback: boolean;
}

export function getLocale(
  languageIdentifier: string,
  supportedLanguages: SupportedLanguages
): LocaleResult {
  const regionalIdentifier = formatLanguageCode(languageIdentifier);

  const [language] = regionalIdentifier.split("-");

  // Check if the language is supported
  if (supportedLanguages[language]) {
    // Check for exact regional match or a match with countryCode
    if (
      regionalIdentifier &&
      supportedLanguages[language].regions
        .map((item) => item.id)
        .includes(regionalIdentifier)
    ) {
      return { locale: regionalIdentifier, usedFallback: false };
    }

    // Fallback to the default for the language
    return {
      locale: supportedLanguages[language].default,
      usedFallback: false,
    };
  }

  // Fallback to first supported language instead of hardcoded 'en'
  const firstSupportedLanguage = Object.values(supportedLanguages)[0]?.default;
  return { locale: firstSupportedLanguage, usedFallback: true };
}

const customDetector = (lng: string) => {
  const determinedLocale = getLocale(lng, supportedLanguages);

  return determinedLocale.locale;
};

const languageNames: Record<string, string> = {
  af: "Afrikaans", // Afrikaans
  ar: "العربية", // Arabic
  bg: "Български", // Bulgarian
  bn: "বাংলা", // Bengali
  ts: "Xitsonga", // Tsonga
  bs: "Bosanski", // Bosnian
  ca: "Català", // Catalan
  cs: "Čeština", // Czech
  da: "Dansk", // Danish
  de: "Deutsch", // German
  el: "Ελληνικά", // Greek
  en: "English", // English
  "es-419": "Español (Latinoamérica)", // Spanish (Latin America)
  "es-ES": "Español", // Spanish (Spain)
  et: "Eesti", // Estonian
  fi: "Suomeksi", // Finnish
  fr: "Français", // French
  "fr-CA": "Français (Canada)", // French (Canada)
  he: "עברית", // Hebrew
  hi: "हिन्दी", // Hindi
  hr: "Hrvatski", // Croatian
  hu: "Magyar", // Hungarian
  hy: "Հայերեն", // Armenian
  it: "Italiano", // Italian (Italy)
  ja: "日本語", // Japanese (Japan)
  ka: "ქართული", // Georgian
  kk: "Қазақша", // Kazakh
  ko: "한국어", // Korean (South Korea)
  lt: "Lietuvių", // Lithuanian
  lv: "Latviešu", // Latvian
  nl: "Nederlands", // Dutch
  no: "Norsk", // Norwegian (Norway)
  pl: "Polski", // Polish
  pt: "Português", // Portuguese
  ro: "Română", // Romanian
  ru: "Русский", // Russian
  sk: "Slovenčina", // Slovak
  sr: "Srpski", // Serbian
  "sr-Cyrl": "Српски (Ћирилица)", // Serbian (Cyrillic)
  st: "Sesotho", // Southern Sotho
  nso: "Sesotho sa Leboa", // Northern Sotho
  sv: "Svenska", // Swedish
  th: "ไทย", // Thai
  tr: "Türkçe", // Turkish
  te: "తెలుగు", // Telugu
  or: "ଓଡ଼ିଆ", // Odia (Oriya)
  uk: "Українська", // Ukrainian
  ur: "اردو", // Urdu
  vi: "Tiếng Việt", // Vietnamese
  xh: "isiXhosa", // Xhosa
  zh: "华语", // Chinese (Simplified)
  zf: "繁體中文", // Traditional Chinese (non-standard code)
  zu: "isiZulu", // Zulu
};

export const getLanguageName = (regionId: string): string => {
  return languageNames[regionId] || regionId;
};

export const supportedLanguagesEnv = ENV.APP.SUPPORTED_LANGUAGES;

export const getSupportedLanguages = (
  supportedCodes: string[] = supportedLanguagesEnv
): SupportedLanguages => {
  const languages: SupportedLanguages = {};

  if (supportedCodes.length === 0) {
    console.error("No supported languages found in environment variables.");
    return languages;
  }

  // Group by base language
  supportedCodes.forEach((code) => {
    const [language] = code.split("-");

    if (!languages[language]) {
      languages[language] = {
        regions: [],
        // The first code listed for a language is only where a saved code that
        // is no longer listed moves to. es-ES stays the default Spanish. es-419
        // is listed first only so participants who saved the old Latin American
        // es move to es-419.
        default: code,
      };
    }

    languages[language].regions.push({
      id: code,
      name: getLanguageName(code),
    });
  });

  return languages;
};

export const supportedLanguages: SupportedLanguages = getSupportedLanguages();

export const getRegionById = (regionId: string): Region | undefined => {
  for (const languageKey in supportedLanguages) {
    const language = supportedLanguages[languageKey];
    const region = language.regions.find((item) => item.id === regionId);
    if (region) {
      return region;
    }
  }

  return undefined;
};

export const getAllRegions = (): Region[] => {
  const regions: Region[] = [];
  Object.values(supportedLanguages).forEach((language) => {
    regions.push(...language.regions);
  });
  return regions;
};

export const changeUserLanguage = (language: string) => {
  const localeResult = getLocale(language ?? "en", supportedLanguages);
  return i18n.changeLanguage(localeResult.locale);
};

const i18nInstance = i18n
  .use(
    resourcesToBackend(
      (language: string) =>
        import(`../../assets/shared/locales/${language}.json`)
    )
  )
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: false,
    load: "currentOnly",
    fallbackLng: supportedLanguagesEnv[0], // Use first supported language as fallback
    supportedLngs: Object.keys(supportedLanguages).flatMap((lang) =>
      supportedLanguages[lang].regions.map((item) => item.id)
    ),
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupQuerystring: "lng",
      convertDetectedLanguage: customDetector,
    },
    react: {
      useSuspense: true,
    },
  });

export default i18nInstance;
